/**
 * Cancel a booking — the target of the cancel link in the confirmation email
 * (/book/cancel?id=...&token=...). Calls booking.cancel with the token.
 */
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui'
import { trpc } from '@/lib/trpc'
import { useSEO } from '@/hooks/useSEO'

type State = 'idle' | 'cancelling' | 'done' | 'error'

interface BookingDetails {
  name: string
  when: string
  label: string
}

export default function CancelBookingPage() {
  useSEO({ title: 'Cancel Booking', description: 'Cancel your Fugue Lab call.', url: '/book/cancel' })
  const [params] = useSearchParams()
  const id = params.get('id') ?? ''
  const token = params.get('token') ?? ''
  const [state, setState] = useState<State>('idle')
  const [details, setDetails] = useState<BookingDetails | null>(null)

  const valid = Boolean(id && token)

  useEffect(() => {
    if (!valid) return
    trpc.booking.getForCancel
      .query({ bookingId: id, cancelToken: token })
      .then(setDetails)
      .catch(() => {
        // Booking may already be gone; the cancel button's own error state covers it.
      })
  }, [valid, id, token])

  async function cancel() {
    setState('cancelling')
    try {
      await trpc.booking.cancel.mutate({ bookingId: id, cancelToken: token })
      setState('done')
    } catch {
      setState('error')
    }
  }

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4 py-20">
      <div className="border-border bg-background w-full max-w-md rounded-xl border p-8 text-center shadow-sm">
        {state === 'done' ? (
          <>
            <h1 className="text-foreground text-2xl font-bold">Booking cancelled</h1>
            <p className="text-muted-foreground mt-3">Your call has been cancelled and removed from the calendar.</p>
            <Button asChild className="mt-6">
              <Link to="/book">Pick a new time</Link>
            </Button>
          </>
        ) : state === 'error' || !valid ? (
          <>
            <h1 className="text-foreground text-2xl font-bold">Couldn't cancel</h1>
            <p className="text-muted-foreground mt-3">
              This cancel link looks invalid or the booking is already gone. Email{' '}
              <a className="text-primary underline" href="mailto:hello@fuguelab.com">
                hello@fuguelab.com
              </a>{' '}
              and we'll sort it out.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-foreground text-2xl font-bold">Cancel your call?</h1>
            {details && (
              <p className="text-muted-foreground mt-2 text-sm">
                {details.label} with {details.name}
                <br />
                {details.when}
              </p>
            )}
            <p className="text-muted-foreground mt-3">This frees the time for someone else.</p>
            <div className="mt-6 flex justify-center gap-3">
              <Button variant="outline" asChild>
                <Link to="/">Keep it</Link>
              </Button>
              <Button variant="destructive" onClick={cancel} disabled={state === 'cancelling'}>
                {state === 'cancelling' ? 'Cancelling…' : 'Cancel booking'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
